/**
 * Data cleaning utilities for family application
 * Handles cascading field clearing when various fields change
 */

/**
 * Define dependency rules - when a field changes, clear these dependent fields
 */
const DEPENDENCY_RULES = {
  branch: [
    'militaryComponent',
    'militaryStatus',
    'orderType',
    'recruiter',
    'orderTitle',
    'orderStartDate',
    'orderEndDate',
    'orderDatesUnknown',
    'grade',
    'gradeUnknown',
    'yearsOfService',
    'paymentSystem',
    'paymentSystemUnknown',
  ],
  militaryComponent: [
    'militaryStatus',
    'orderType',
    'recruiter',
    'orderTitle',
    'orderStartDate',
    'orderEndDate',
    'orderDatesUnknown',
    'grade',
    'gradeUnknown',
    'yearsOfService',
    'paymentSystem',
    'paymentSystemUnknown',
  ],
  militaryStatus: [
    'orderType',
    'recruiter',
    'orderTitle',
    'orderStartDate',
    'orderEndDate',
    'orderDatesUnknown',
    'grade',
    'gradeUnknown',
    'yearsOfService',
    'paymentSystem',
    'paymentSystemUnknown',
  ],
  orderType: ['recruiter', 'orderStartDate', 'orderEndDate', 'orderDatesUnknown'],
  orderTitle: ['orderStartDate', 'orderEndDate', 'orderDatesUnknown'],
};

/**
 * Gets the cleaning rules for dependent fields when a specific field changes
 * @param {string} changedField - The field that changed
 * @param {Object} existingMilitaryDetails - Current military details object
 * @returns {Object} Object with fields to clear set to null
 */
export const getMilitaryFieldCleaning = (changedField, existingMilitaryDetails = {}) => {
  const cleaning = {};

  // Get dependent fields that should be cleared
  const fieldsToClear = DEPENDENCY_RULES[changedField] || [];

  // Set dependent fields to null
  fieldsToClear.forEach((field) => {
    cleaning[field] = null;
  });

  return cleaning;
};

/**
 * Applies military field cleaning rules (always clears dependent fields)
 * @param {string} changedField - The field that changed ('branch', 'militaryComponent', etc.)
 * @param {*} newValue - The new value for the field (primitive or object)
 * @param {Object} existingMilitaryDetails - Current military details object
 * @returns {Object} Updated military details with cleaning applied
 */
export const applyMilitaryFieldCleaning = (changedField, newValue, existingMilitaryDetails = {}) => {
  const updatedDetails = { ...existingMilitaryDetails };

  // Convert primitive values to object format for fields that expect { id: value }
  const shouldUseObjectFormat = [
    'branch',
    'militaryComponent',
    'militaryStatus',
    'orderType',
    'orderTitle',
    'grade',
    'paymentSystem',
  ].includes(changedField);

  if (shouldUseObjectFormat && newValue !== null && newValue !== undefined) {
    // If newValue is already an object, use it as-is, otherwise wrap in { id: value }
    updatedDetails[changedField] = typeof newValue === 'object' && newValue !== null ? newValue : { id: newValue };
  } else {
    // For primitive fields or null/undefined values
    updatedDetails[changedField] = newValue;
  }

  // Apply cleaning rules
  const cleaning = getMilitaryFieldCleaning(changedField);
  Object.assign(updatedDetails, cleaning);

  return updatedDetails;
};

/**
 * Helper function for updating military details in components
 * Steps should check if the value changed before calling this
 * @param {Object} params - Parameters object
 * @param {string} params.fieldName - The field being updated
 * @param {*} params.newValue - The new value
 * @param {Object} params.application - Full application object
 * @returns {Object} Updated military details object
 */
export const updateMilitaryField = ({ fieldName, newValue, application }) => {
  const currentMilitaryDetails = application?.applicant?.militaryDetails || {};
  return applyMilitaryFieldCleaning(fieldName, newValue, currentMilitaryDetails);
};
